/**
 * Walk-Forward Testing Panel Component
 * Version: 6.2.0
 */
import { useState } from 'react'
import './WalkForwardPanel.css'
import { quickWalkForwardAnalysis, OHLCV } from '../utils/walkforward/WalkForwardEngine'
import WalkForwardVisualization from './WalkForwardVisualization'

interface WFAnalysisResult {
  config: { trainSize: number; testSize: number; stepSize: number; mode: string }
  windows: Array<{
    windowIndex: number
    startDate: number
    endDate: number
    trainStart: number
    trainEnd: number
    testStart: number
    testEnd: number
    metrics: {
      trainReturn: number
      trainSharpe: number
      trainWinRate: number
      trainMaxDrawdown: number
      testReturn: number
      testSharpe: number
      testWinRate: number
      testMaxDrawdown: number
      wfRatio: number
      returnConsistency: number
      sharpeConsistency: number
    }
    equityCurve: number[]
    trades: Array<{
      entryDate: number
      exitDate: number
      entryPrice: number
      exitPrice: number
      type: string
      pnl: number
      returnPct: number
    }>
  }>
  aggregateMetrics: {
    avgReturn: number
    avgSharpe: number
    avgWinRate: number
    avgDrawdown: number
    totalTrades: number
    winRate: number
    maxDrawdown: number
    sharpeStdDev: number
    returnStdDev: number
  }
  bestWindow: number
  worstWindow: number
  overallEquity: number[]
}

function WalkForwardPanel() {
  const [isRunning, setIsRunning] = useState(false)
  const [results, setResults] = useState<WFAnalysisResult | null>(null)
  const [config, setConfig] = useState({
    trainSize: 60,
    testSize: 20,
    stepSize: 10,
    mode: 'rolling' as 'rolling' | 'anchored'
  })

  // Generate sample data for demonstration
  const generateSampleData = (): OHLCV[] => {
    const data: OHLCV[] = []
    const now = Date.now()
    let price = 100
    
    for (let i = 0; i < 500; i++) {
      const timestamp = now - (500 - i) * 24 * 60 * 60 * 1000
      const change = (Math.random() - 0.5) * 5
      price = Math.max(50, price + change)
      
      data.push({
        timestamp,
        open: price - Math.random() * 2,
        high: price + Math.random() * 3,
        low: price - Math.random() * 3,
        close: price,
        volume: Math.floor(Math.random() * 1000000) + 100000
      })
    }
    
    return data
  }

  const runAnalysis = async () => {
    setIsRunning(true)
    
    // Simulate analysis time
    await new Promise(resolve => setTimeout(resolve, 1500))
    
    const sampleData = generateSampleData()
    const analysisResults = quickWalkForwardAnalysis(
      sampleData,
      config.trainSize,
      config.testSize,
      config.stepSize,
      config.mode
    )
    
    setResults(analysisResults as unknown as WFAnalysisResult)
    setIsRunning(false)
  }

  return (
    <div className="wf-panel">
      <div className="wf-panel-header">
        <div className="wf-panel-title">
          <span className="wf-icon">📊</span>
          <div>
            <h3>Walk-Forward Analysis</h3>
            <p>Test strategy robustness across multiple time windows</p>
          </div>
        </div>
        <div className="wf-badge">
          <span className="badge-icon">🔬</span>
          <span>Advanced Testing</span>
        </div>
      </div>

      {/* Configuration */}
      <div className="wf-config-section">
        <h4>Configuration</h4>
        <div className="wf-config-grid">
          <div className="config-item">
            <label>Training Period (In-Sample)</label>
            <div className="input-group">
              <input 
                type="number" 
                value={config.trainSize}
                onChange={(e) => setConfig({ ...config, trainSize: parseInt(e.target.value) || 30 })}
                min="20"
                max="200"
              />
              <span className="input-suffix">periods</span>
            </div>
            <span className="config-hint">Longer = more data for optimization</span>
          </div>
          
          <div className="config-item">
            <label>Testing Period (Out-of-Sample)</label>
            <div className="input-group">
              <input 
                type="number" 
                value={config.testSize}
                onChange={(e) => setConfig({ ...config, testSize: parseInt(e.target.value) || 10 })}
                min="5"
                max="100"
              />
              <span className="input-suffix">periods</span>
            </div>
            <span className="config-hint">Tests real performance</span>
          </div>
          
          <div className="config-item">
            <label>Step Size (Walk Forward)</label>
            <div className="input-group">
              <input 
                type="number" 
                value={config.stepSize}
                onChange={(e) => setConfig({ ...config, stepSize: parseInt(e.target.value) || 10 })}
                min="1"
                max="50"
              />
              <span className="input-suffix">periods</span>
            </div>
            <span className="config-hint">Smaller = more windows</span>
          </div>
          
          <div className="config-item">
            <label>Window Mode</label>
            <div className="mode-toggle">
              <button 
                className={`mode-btn ${config.mode === 'rolling' ? 'active' : ''}`}
                onClick={() => setConfig({ ...config, mode: 'rolling' })}
              >
                🔄 Rolling
              </button>
              <button 
                className={`mode-btn ${config.mode === 'anchored' ? 'active' : ''}`}
                onClick={() => setConfig({ ...config, mode: 'anchored' })}
              >
                ⚓ Anchored
              </button>
            </div>
            <span className="config-hint">
              {config.mode === 'rolling' ? 'Move forward each window' : 'Start from beginning each window'}
            </span>
          </div>
        </div>
      </div>

      {/* Run Button */}
      <div className="wf-run-section">
        <button 
          className={`run-btn ${isRunning ? 'running' : ''}`}
          onClick={runAnalysis}
          disabled={isRunning}
        >
          {isRunning ? (
            <>
              <span className="spinner" />
              Analyzing...
            </>
          ) : (
            <>
              <span className="btn-icon">▶️</span>
              Run Walk-Forward Analysis
            </>
          )}
        </button>
        
        {!isRunning && !results && (
          <p className="run-hint">
            💡 This will analyze your strategy across multiple in-sample and out-of-sample windows
          </p>
        )}
      </div>

      {/* Results */}
      {results && (
        <WalkForwardVisualization results={results} />
      )}

      {/* Info */}
      <div className="wf-info">
        <h4>📚 What is Walk-Forward Testing?</h4>
        <p>
          Walk-forward testing is a rigorous method to validate trading strategies. It divides historical 
          data into multiple windows, using each window's training period to optimize parameters and the 
          testing period to measure real performance.
        </p>
        <ul>
          <li><strong>Prevents Overfitting:</strong> Out-of-sample testing shows true performance</li>
          <li><strong>Robustness Check:</strong> Consistent results across windows indicate reliability</li>
          <li><strong>WF Ratio:</strong> Test Sharpe / Train Sharpe (closer to 1 = better)</li>
        </ul>
      </div>
    </div>
  )
}

export default WalkForwardPanel